import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import './OrderSummary.css';
import CouponModal from './CouponModal';
import axios from 'axios';
import { API_BASE_URL } from '../utils';

const OrderSummary = () => {
  const [useCarrot, setUseCarrot] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMore, setViewMore] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const sourcePage = searchParams.get("sourcePage");


  const userData = JSON.parse(localStorage.getItem('userData') || '{}');

  // Calculate amounts based on backend response structure
  const calculateAmounts = useCallback(() => {
    if (!orderData) return null;

    const baseAmount = parseFloat(orderData.total_amount || 0);
    const payableAmount = parseFloat(orderData.payable_amount || 0); // pw_comm_amount + club_advance
    const netPayableAmount = parseFloat(orderData.net_payable_amount || 0); // includes platform_fee
    const dueAmount = parseFloat(orderData.due_amount || 0);
    const usableCarrot = parseFloat(orderData.usableCarrot || 0);
    const platformFee = parseFloat(orderData.platform_fee || 0);
    const clubAdvance = parseFloat(orderData.club_advance_amount || 0);
    const pwCommAmount = parseFloat(orderData.pw_total_comm_amount || 0);

    // Calculate coupon discount if applied
    let couponDiscount = 0;
    if (selectedCoupon) {
      const minSpent = parseFloat(selectedCoupon.min_spent || 0);
      if (payableAmount >= minSpent) {
        if (selectedCoupon.discount_type === 'percent') {
          couponDiscount = (payableAmount * parseFloat(selectedCoupon.discount)) / 100;
        } else {
          couponDiscount = parseFloat(selectedCoupon.discount);
        }

        if (selectedCoupon.capping) {
          couponDiscount = Math.min(couponDiscount, parseFloat(selectedCoupon.capping));
        }
      }
    }

    // Calculate carrot discount
    const carrotDiscount = useCarrot ? Math.min(usableCarrot, payableAmount * 0.1) : 0;

    // Final amounts after discounts
    const finalPayableAmount = netPayableAmount - carrotDiscount - couponDiscount;

    return {
      baseAmount,
      payableAmount,
      netPayableAmount,
      dueAmount,
      usableCarrot,
      platformFee,
      clubAdvance,
      pwCommAmount,
      carrotDiscount,
      couponDiscount,
      finalPayableAmount: Math.max(0, finalPayableAmount)
    };
  }, [orderData, useCarrot, selectedCoupon]);

  const amounts = calculateAmounts();

  const createOrder = useCallback(async () => {
    if (!id || !userData?.id) {
      setError('Missing required parameters');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const formData = new FormData();
      formData.append('bid_id', id);
      formData.append('user_id', userData.id);
      formData.append('amount', null);
      formData.append('type', 'discount');

      const url = sourcePage === "table" ? `${API_BASE_URL}/master/APIs/ClubTable/placeOrder` : `${API_BASE_URL}/master/APIs/ClubPackage/placeOrder`;
      const response = await axios.post(
        url,
        formData,
      );

      const responseData = response.data;

      if (responseData.status === true && responseData.data?.length > 0) {
        setOrderData(responseData.data[0]);
        setAvailableCoupons(responseData.available_coupon || []);
      } else {
        setError(responseData.msg || 'Failed to create order');
      }
    } catch (error) {
      console.error('Order creation error:', error);
      if (error.code === 'ECONNABORTED') {
        setError('Request timeout. Please try again.');
      } else if (error.response) {
        setError(`Server error: ${error.response.status}`);
      } else if (error.request) {
        setError('Network error. Please check your connection.');
      } else {
        setError('An unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
  }, [id, userData?.id]);

  useEffect(() => {
    createOrder();
  }, [createOrder]);

  const handleCouponApply = (coupon) => {
    setSelectedCoupon(coupon);
    setShowModal(false);
  };

  const handleRemoveCoupon = () => {
    setSelectedCoupon(null);
  };

  const handleProceedToCheckout = () => {
    if (!orderData?.order_id) {
      setError('Order ID not found');
      return;
    }

    // Navigate to checkout with order details
    navigate(`/checkout/${orderData?.order_id}?sourcePage=${sourcePage}`, {
      state: {
        orderData,
        useCarrot,
        selectedCoupon,
        amounts
      }
    });
  };


  if (loading) {
    return (
      <div className="bid-order-summery-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Creating your order...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bid-order-summery-page">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>Oops! Something went wrong</h3>
          <p>{error}</p>
          <button onClick={createOrder} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!orderData || !amounts) {
    return (
      <div className="bid-order-summery-page">
        <div className="error-container">
          <p>No order data available</p>
          <button onClick={() => navigate(-1)} className="back-btn">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='order-summery-page'>
      <div className="bid-order-summery-page">
        {/* Price Summary */}
        <div className="price-summary-card">
          <div className='summary-title-section'>
            <h3 className="summary-title">Order Summary</h3>
          </div>

          <div className="booking-details-card">
            <div className="order-id">
              <p>Order ID: <strong>{orderData?.order_id}</strong></p>
            </div>
            <div className="booking-info">
              <div className="info-row">
                <span>Date: </span>
                <span>{orderData.party_date}</span>
              </div>
              <div className="info-row">
                <span>Time: </span>
                <span>{orderData.party_time}</span>
              </div>
              {Number(orderData.male_qty) > 0 && (
                <div className="info-row">
                  <span>Male:</span>
                  <span>{orderData.male_qty} × ₹{orderData.male_amount}</span>
                </div>
              )}
              {Number(orderData.female_qty) > 0 && (
                <div className="info-row">
                  <span>Female: </span>
                  <span>{orderData.female_qty} × ₹{orderData.female_amount}</span>
                </div>
              )}
              {Number(orderData.kids_qty) > 0 && (
                <div className="info-row">
                  <span>Kids:</span>
                  <span>{orderData.kids_qty} × ₹{orderData.kids_amount}</span>
                </div>
              )}
            </div>
          </div>



          <div className="summary-amounts">

            <div className="summary-line">
              <span>Total Package Amount</span>
              <span>₹{amounts.baseAmount.toFixed(2)}</span>
            </div>

            {viewMore && (
              <>
                <div className="summary-line">
                  <span>Club Advance (20%)</span>
                  <span>₹{amounts.clubAdvance.toFixed(2)}</span>
                </div>

                <div className="summary-line">
                  <span>Platform Commission</span>
                  <span>₹{amounts.pwCommAmount.toFixed(2)}</span>
                </div>

                <div className="summary-line">
                  <span>Platform Fee</span>
                  <span>₹{amounts.platformFee.toFixed(2)}</span>
                </div>
              </>)}
            <span className="summary-title-view-more" onClick={() => setViewMore(!viewMore)}>{viewMore ? 'View Less' : 'View More'}</span>


            {/* Carrot Section */}
            {amounts.usableCarrot > 0 && (
              <div className="carrot-section">
                <div className="carrot-header">
                  <span className="carrot-icon">🥕</span>
                  <span>Available Carrot Balance: {amounts.usableCarrot}</span>
                </div>
                <label className="carrot-checkbox">
                  <input
                    type="checkbox"
                    checked={useCarrot}
                    onChange={(e) => setUseCarrot(e.target.checked)}
                  />
                  <span>Use Carrot Balance</span>
                  {useCarrot && (
                    <span className="carrot-discount">(-₹{amounts.carrotDiscount.toFixed(2)})</span>
                  )}
                </label>
              </div>
            )}

            {/* Coupon Section */}
            <div className="coupon-section">
              <div className="coupon-input-group">
                <input
                  type="text"
                  className="coupon-input"
                  value={selectedCoupon?.code || ''}
                  placeholder="Enter coupon code"
                  readOnly
                />
                <button
                  className="coupon-btn"
                  onClick={() => setShowModal(true)}
                >
                  {selectedCoupon ? 'Change' : 'Apply'}
                </button>
              </div>

              {selectedCoupon && (
                <div className="coupon-applied">
                  <span className="coupon-success">✅ Coupon Applied!</span>
                  <span className="coupon-discount">You saved ₹{amounts.couponDiscount.toFixed(2)}</span>
                  <button onClick={handleRemoveCoupon} className="remove-coupon">Remove</button>
                </div>
              )}
            </div>
            <div className="summary-line total">
              <strong>Amount to Pay Now</strong>
              <strong>₹{amounts.netPayableAmount.toFixed(2)}</strong>
            </div>

            <div className="summary-line pay-later">
              <span>Pay at Venue</span>
              <span>₹{amounts.dueAmount.toFixed(2)}</span>
            </div>
          </div>



          {/* Final Amount */}
          {(amounts.carrotDiscount > 0 || amounts.couponDiscount > 0) && (
            <div className="final-amount-section">
              <hr />
              <div className="summary-line final-total">
                <strong>Final Amount to Pay</strong>
                <strong className="final-price">₹{amounts.finalPayableAmount.toFixed(2)}</strong>
              </div>
            </div>
          )}
        </div>

        <div className='proceed-to-payment-section'>
          <div className='proceed-to-payment-wrapper'>
            {(amounts.carrotDiscount > 0 || amounts.couponDiscount > 0) ? (
              <div className="">
                <h2 >₹{amounts.finalPayableAmount.toFixed(2)}</h2>
              </div>
            ) : (<div className="">
              <h2>₹{amounts.netPayableAmount.toFixed(2)}</h2>
            </div>)}

            <button
              className="proceed-btn"
              onClick={handleProceedToCheckout}
              disabled={!orderData?.order_id}
            >
              Proceed to Payment
            </button>
          </div>

        </div>

        {/* Coupon Modal */}
        <CouponModal
          show={showModal}
          onClose={() => setShowModal(false)}
          availableCoupons={availableCoupons}
          onApplyCoupon={handleCouponApply}
          selectedCoupon={selectedCoupon}
          orderAmount={amounts?.payableAmount || 0}
        />
      </div>
    </div>
  );
};

export default OrderSummary;